import { useCallback, useEffect, useState } from 'react'
import { X, RotateCcw } from 'lucide-react'
import * as Diff from 'diff'
import { fetchPageRevisions, fetchRevision, restoreRevision } from '../api/client'
import type { WikiRevision } from '../types/wiki'

interface RevisionHistoryProps {
  slug: string
  open: boolean
  onClose: () => void
  currentMarkdown: string
  canRestore: boolean
  onRestored: () => void
}

export default function RevisionHistory({
  slug,
  open,
  onClose,
  currentMarkdown,
  canRestore,
  onRestored,
}: RevisionHistoryProps) {
  const [revisions, setRevisions] = useState<WikiRevision[]>([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [diffMarkdown, setDiffMarkdown] = useState<string | null>(null)
  const [diffLoading, setDiffLoading] = useState(false)
  const [restoring, setRestoring] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setErr(null)
    try {
      const { revisions: items } = await fetchPageRevisions(slug)
      setRevisions(items)
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error ||
            'Failed to load revisions'
          : 'Failed to load revisions'
      setErr(msg)
      setRevisions([])
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    if (!open) {
      setSelectedId(null)
      setDiffMarkdown(null)
      return
    }
    void load()
  }, [open, load])

  const viewDiff = async (revisionId: string) => {
    setSelectedId(revisionId)
    setDiffLoading(true)
    setDiffMarkdown(null)
    try {
      const detail = await fetchRevision(slug, revisionId)
      setDiffMarkdown(detail.markdown)
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error ||
            'Failed to load revision'
          : 'Failed to load revision'
      setErr(msg)
    } finally {
      setDiffLoading(false)
    }
  }

  const handleRestore = async (revisionId: string) => {
    const password = window.prompt('Enter your password to restore this revision:')
    if (password === null) return
    setRestoring(true)
    try {
      await restoreRevision(slug, revisionId, password)
      onRestored()
      onClose()
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Restore failed'
          : 'Restore failed'
      window.alert(msg)
    } finally {
      setRestoring(false)
    }
  }

  const diffParts =
    diffMarkdown !== null ? Diff.diffLines(diffMarkdown, currentMarkdown) : []

  if (!open) return null

  return (
    <div
      className="no-print fixed inset-0 z-50 flex justify-end bg-black/50"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl h-full bg-gray-950 border-l border-gray-800 shadow-xl overflow-y-auto flex flex-col"
        role="dialog"
        aria-label="Revision history"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-800 sticky top-0 bg-gray-950 z-10">
          <h2 className="text-lg font-semibold text-gray-100">Revision history</h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 p-4 space-y-4">
          {err && <p className="text-error-400 text-sm">{err}</p>}
          {loading && <p className="text-gray-400 text-sm">Loading revisions…</p>}

          {!loading && revisions.length === 0 && (
            <p className="text-gray-500 text-sm">No revisions recorded yet.</p>
          )}

          <ul className="space-y-2">
            {revisions.map((rev) => {
              const when = new Date(rev.created_at)
              const whenLabel = Number.isNaN(when.getTime()) ? rev.created_at : when.toLocaleString()
              return (
                <li
                  key={rev.id}
                  className={`rounded-lg border p-3 ${
                    selectedId === rev.id ? 'border-primary-700/50 bg-primary-950/20' : 'border-gray-800 bg-gray-900/40'
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-sm text-gray-200">{rev.summary || 'Edit'}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {rev.author ?? 'Unknown'} · <time dateTime={rev.created_at}>{whenLabel}</time>
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => void viewDiff(rev.id)}
                        className="text-xs font-medium min-h-[44px] px-3 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800"
                      >
                        View diff
                      </button>
                      {canRestore && (
                        <button
                          type="button"
                          disabled={restoring}
                          onClick={() => void handleRestore(rev.id)}
                          className="inline-flex items-center gap-1 text-xs font-medium min-h-[44px] px-3 rounded-lg border border-amber-800/50 text-amber-200 hover:bg-amber-950/30 disabled:opacity-50"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                          Restore
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>

          {selectedId && (
            <div className="rounded-lg border border-gray-700 bg-gray-900/60 p-3">
              <p className="text-sm font-medium text-gray-300 mb-2">Diff (revision → current)</p>
              {diffLoading && <p className="text-gray-500 text-sm">Loading diff…</p>}
              {!diffLoading && diffParts.length > 0 && (
                <pre className="text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                  {diffParts.map((part, i) => (
                    <span
                      key={i}
                      className={
                        part.added
                          ? 'bg-green-950/50 text-green-300'
                          : part.removed
                            ? 'bg-red-950/50 text-red-300 line-through'
                            : 'text-gray-400'
                      }
                    >
                      {part.value}
                    </span>
                  ))}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
